<script lang="ts">
  import { getContext } from "svelte";
  import { Check } from "$components/+blocks/OVIcon";
  import { selectedKey, type Select } from "./OptionGroup.svelte";
  import InnerContainer from "../internal/InnerContainer.svelte";
  import OptionContainer from "../internal/OptionContainer.svelte";
  import TitleDescription from "../internal/TitleDescription.svelte";
  import OVIcon from "$components/+blocks/OVIcon/OVIcon.svelte";
  import Avatar from "$components/+blocks/Avatar/Avatar.svelte";
  import type { IconType } from "$components/+blocks/OVIcon/OVIcon";
  import type { Writable } from "svelte/store";

  export let title: string;
  export let value: string | object | undefined = title;
  export let key: (x: any) => string = (x) => x;
  export let description: string = "";
  export let icon: IconType | undefined = undefined;
  export let iconColor: string | undefined = undefined;
  export let checkedIcon: IconType = Check;
  export let closeOnTrigger: boolean = true;
  export let avatar: string | undefined = undefined;
  export let showAvatar: boolean = false;
  export let reverseOrder: boolean = false;
  export let tooltipCopy: string = "";

  const selected = getContext<Writable<Select>>(selectedKey);
  // if just the checkbox is selected don't close the dropdown
  // if the whole button is clicked, close the dropdown
  let wasCheckboxClicked = false;

  $: isSelected = $selected?.selected?.some((item) => key(item) === key(value));
  $: isTailwindClass = iconColor?.includes("text-");
</script>

<OptionContainer
  {title}
  {value}
  {key}
  {closeOnTrigger}
  on:click
  on:click={() => {
    if (!wasCheckboxClicked) {
      closeOnTrigger = true;
    }
    wasCheckboxClicked = false;
  }}
>
  <InnerContainer {description} {isSelected} {reverseOrder} {tooltipCopy}>
    <div class="relative leading-[0]">
      <!-- add an outer container on top to make the checkbox area double size larger -->
      <button
        class="absolute -top-2 -left-2 size-8 cursor-pointer"
        on:click={() => {
          wasCheckboxClicked = true;
          closeOnTrigger = false;
        }}
        on:keydown={() => {
          wasCheckboxClicked = true;
          closeOnTrigger = false;
        }}
      />
      <span
        class:mt-0.5={description}
        class:dark:bg-neutral-400={isSelected}
        class:dark:group-hover:bg-neutral-500={!isSelected}
        class:bg-neutral-500={isSelected}
        class:group-hover:bg-neutral-400={!isSelected}
        class="inline-flex size-4 items-center justify-center rounded border border-black/15 text-neutral-50 dark:border-white/20 dark:text-neutral-900"
        role="checkbox"
        aria-checked={isSelected}
        aria-label={title}
        tabindex="0"
      >
        {#if isSelected}
          <OVIcon icon={checkedIcon} />
        {/if}
      </span>
    </div>

    <div class="inline-flex items-center gap-1.5">
      {#if icon}
        <span
          class:mr-1.5={reverseOrder}
          class={isTailwindClass ? iconColor : undefined}
          style={!isTailwindClass && iconColor ? `color: ${iconColor};` : undefined}
          class:opacity-70={!isSelected}
          class:dark:opacity-60={!isSelected}
        >
          <OVIcon {icon} />
        </span>
      {/if}
      {#if showAvatar}
        <Avatar radius="rounded-[3px]" mainImage={avatar} label={title[0]} />
      {/if}
      <TitleDescription {title} {description} />
    </div>
  </InnerContainer>
</OptionContainer>
